import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { MOBILE_BREAKPOINT } from '../../constants/global';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './pdf-preview.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfPreviewProps = {
	file: string;
	className?: string;
	loadImmediately?: boolean;
};

const PdfPreview = ({ file, className = '', loadImmediately = false }: PdfPreviewProps) => {
	const previewRef = useRef<HTMLDivElement>(null);
	const isModalPreview = className.includes('pdf-preview--modal');
	const [pageCount, setPageCount] = useState<number>();
	const [pageRatio, setPageRatio] = useState(0.7);
	const [pageWidth, setPageWidth] = useState<number>();
	const [isMobilePreview, setIsMobilePreview] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
	const [shouldLoadPdf, setShouldLoadPdf] = useState(isModalPreview || loadImmediately);

	useEffect(() => {
		if (isModalPreview || loadImmediately || shouldLoadPdf) {
			return;
		}

		const preview = previewRef.current;
		if (!preview || !('IntersectionObserver' in window)) {
			setShouldLoadPdf(true);
			return;
		}

		const pageViewport = document.querySelector<HTMLElement>('.page-viewport');
		const observerRoot = pageViewport?.contains(preview) ? pageViewport : null;
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setShouldLoadPdf(true);
				observer.disconnect();
			}
		}, {
			root: observerRoot,
			rootMargin: '300px 0px',
		});

		observer.observe(preview);
		return () => observer.disconnect();
	}, [isModalPreview, loadImmediately, shouldLoadPdf]);

	useEffect(() => {
		const updatePageWidth = () => {
			setIsMobilePreview(window.innerWidth <= MOBILE_BREAKPOINT);
			const availableWidth = previewRef.current?.clientWidth ?? window.innerWidth - 40;
			const availableHeight = window.innerHeight - 100;
			const width = isModalPreview
				? Math.min(availableWidth, availableHeight * pageRatio)
				: availableWidth;
			setPageWidth(Math.max(1, Math.floor(width)));
		};

		updatePageWidth();
		const preview = previewRef.current;
		const resizeObserver = preview && 'ResizeObserver' in window
			? new ResizeObserver(updatePageWidth)
			: null;
		if (preview && resizeObserver) {
			resizeObserver.observe(preview);
		}
		window.addEventListener('resize', updatePageWidth);
		return () => {
			window.removeEventListener('resize', updatePageWidth);
			resizeObserver?.disconnect();
		};
	}, [isModalPreview, pageRatio]);

	return (
		<div ref={previewRef} className={`pdf-preview ${className}`.trim()}>
			{shouldLoadPdf && pageWidth ? (
				<>
					<Document
						file={file}
						onLoadSuccess={async (pdf) => {
							setPageCount(pdf.numPages);
							const page = await pdf.getPage(1);
							const viewport = page.getViewport({ scale: 1 });
							setPageRatio(viewport.width / viewport.height);
						}}
						loading={<span className="pdf-preview__status">Loading PDF...</span>}
						error={<span className="pdf-preview__status">Unable to load PDF.</span>}
					>
						<Page
							pageNumber={1}
							width={pageWidth}
							devicePixelRatio={isModalPreview ? window.devicePixelRatio || 1 : isMobilePreview ? 0.60 : 1}
							renderAnnotationLayer
							renderTextLayer
						/>
					</Document>
					{pageCount && (
						<span className="pdf-preview__page-count">1 / {pageCount}</span>
					)}
				</>
			) : (
				<span className="pdf-preview__status">
					{shouldLoadPdf ? 'Loading PDF...' : 'PDF preview loads when visible.'}
				</span>
			)}
		</div>
	);
};

export default PdfPreview;
